"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.propTypes = exports.getIconSize = exports.defaultProps = exports.default = exports.Link = void 0;
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _conditionalAttributes = require("../../util/conditionalAttributes/conditionalAttributes");
var _Icon = _interopRequireDefault(require("../Icon/Icon"));
var _Text = _interopRequireDefault(require("../Text/Text"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
// TODO: &:visited may not be able to change other psuedo elements or child elements. Need to explore other solutions - https://stackoverflow.com/questions/16180422/cannot-change-the-content-of-visited-before-pseudo-elements

/**
 * Link Component
 * @method
 * @param {object} props - React properties passed from composition
 * @returns Link
 */
/**
 * Primary UI component for user interaction
 */
const Link = props => {
  const {
    ariaLabel,
    children = defaultProps.children,
    className,
    disabled = defaultProps.disabled,
    fullyClickableParent,
    fullWidth = defaultProps.fullWidth,
    href = defaultProps.href,
    iconName = defaultProps.iconName,
    iconPosition = defaultProps.iconPosition,
    iconSize,
    isKnockoutLink = defaultProps.isKnockoutLink,
    id,
    label,
    linkRef = defaultProps.linkRef,
    screenReaderText = defaultProps.screenReaderText,
    size = defaultProps.size,
    tabIndex,
    target = defaultProps.target,
    textStyle,
    variant = defaultProps.variant,
    ...other
  } = props;
  const isArrow = variant === 'arrow';
  const isClickable = href && !disabled;
  const positionBefore = iconPosition === 'before';
  const positionAfter = iconPosition === 'after';
  const linkTextStyle = (variant === 'hiddenLink' || variant === 'arrow') && textStyle;
  const isButtonStyle = variant === 'buttonPrimary' || variant === 'buttonSecondary' || variant === 'buttonTertiary' || variant === 'buttonPrimaryInverse' || variant === 'buttonAi';
  const isOtherLinkStyle = variant === 'hiddenLink' || variant === 'absoluteLink' || isArrow;
  const derivedIconSize = getIconSize({
    iconSize,
    variant
  });
  const canBeResized = size && !isOtherLinkStyle;
  const isFullWidth = fullWidth && isButtonStyle;
  const showLabel = label && !screenReaderText;
  const componentClassName = (0, _classnames.default)('pal-c-Link', {
    [`pal-c-Link--${variant}`]: variant,
    'pal-c-Link--knockout': isKnockoutLink === true,
    'pal-c-Link--disabled': disabled === true,
    'pal-c-Link--iconName': iconName,
    [`pal-c-Link--${size}`]: canBeResized,
    [`pal-c-Link__style--${textStyle}`]: linkTextStyle,
    'pal-c-Link--fullWidth': isFullWidth,
    'pal-c-Link--absolute': fullyClickableParent,
    'pal-c-Link--buttonStyle': isButtonStyle,
    [`${className}`]: className
  });

  /*
   * Create Icon element if value for icon is passed.
   */
  const computedIcon = isArrow ? /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement("span", {
    className: "pal-c-Link__icon-after"
  }, /*#__PURE__*/_react.default.createElement(_Icon.default, {
    "aria-hidden": "true",
    focusable: false,
    name: "arrowForward",
    size: derivedIconSize
  }))) : iconName && /*#__PURE__*/_react.default.createElement("span", {
    className: `pal-c-Link__icon-${iconPosition}`
  }, /*#__PURE__*/_react.default.createElement(_Icon.default, {
    "aria-hidden": "true",
    focusable: false,
    name: iconName,
    size: derivedIconSize,
    disabled: disabled
  }));
  const conditionalProps = (0, _conditionalAttributes.conditionalAttributes)({
    tabIndex: Number.isInteger(tabIndex) ? tabIndex : undefined,
    'aria-label': ariaLabel || undefined,
    href: isClickable ? href : undefined
  });
  const linkProps = {
    className: componentClassName,
    target,
    ref: linkRef,
    id,
    ...conditionalProps,
    ...other
  };
  const content = fullyClickableParent ? /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, children, showLabel && /*#__PURE__*/_react.default.createElement(_Text.default, {
    htmlTag: "span",
    screenReader: true
  }, label), screenReaderText && /*#__PURE__*/_react.default.createElement(_Text.default, {
    htmlTag: "span",
    screenReader: true
  }, screenReaderText)) : /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, positionBefore && computedIcon, (label || children) && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-Link__label"
  }, label, children), screenReaderText && /*#__PURE__*/_react.default.createElement(_Text.default, {
    htmlTag: "span",
    screenReader: true
  }, screenReaderText), (positionAfter || isArrow) && computedIcon);
  return /*#__PURE__*/_react.default.createElement("a", linkProps, content);
};

/**
 * Gets icon size based on if the icon size prop is passed or set the icon size based on the variant
 * @method
 * @param {Object} props - React properties passed from composition
 * @returns {string} iconSize
 */
exports.Link = Link;
const getIconSize = data => {
  const {
    iconSize,
    variant
  } = data;
  if (iconSize) {
    return iconSize;
  }
  if (variant === 'arrow') {
    return 'lg';
  } else {
    return 'default';
  }
};

/**
 * Property type definitions
 * @type {object}
 * @property {string} ariaLabel - Sets the string in cases where text label is not visible on the screen
 * @property {node} children - Children of the component
 * @property {boolean} disabled - Disables the field and prevents editing the contents
 * @property {boolean} fullWidth - Toggles button that fills the full width of its container
 * @property {string} href - Link to URL
 * @property {string} iconName - Name of SVG iconName (i.e. caretDown, minus, warning)
 * @property {string} iconPosition - sets the icon position (before or after)
 * @property {string} iconSize - sets the size of the icon
 * @property {string} id - sets the id
 * @property {bool} isKnockoutLink - Knockout styling for an inverted link
 * @property {any} linkRef - Reference to the link
 * @property {string} screenReaderText - Visually hidden additional instruction text to help provide screen reader users additional context.
 * @property {string} size - Available size variations for the link and button (default, compact, tiny)
 * @property {number} tabIndex - sets the tabIndex, typically 0 or -1, for Aria support
 * @property {string} target - Target attribute for a link (i.e. set to _blank to open in new tab)
 * @property {string} textStyle - Style of text for the hiddenLink
 * @property {string} variant - Available _stylistic_ variations available for the Link component (primary, secondary, tertiary, arrow, hiddenLink, absoluteLink, buttonPrimary, buttonSecondary, buttonTertiary, buttonAi)
 */
exports.getIconSize = getIconSize;
const propTypes = exports.propTypes = {
  /**
   * Sets the string in cases where text label is not visible on the screen
   */
  ariaLabel: _propTypes.default.string,
  /**
   * Child node(s) that can be nested inside component
   */
  children: _propTypes.default.node,
  /**
   * Remove pointer-events from link
   */
  disabled: _propTypes.default.bool,
  /**
   * URL or a URL fragment string that the hyperlink points to
   */
  href: _propTypes.default.string,
  /**
   * Knockout variant for component rendered on a dark background
   */
  isKnockoutLink: _propTypes.default.bool,
  /**
   * Link reference
   */
  linkRef: _propTypes.default.any,
  /**
   * Render an icon with the anchor tag.
   */
  iconName: _propTypes.default.string,
  /**
   * Placement of icon relative to text
   */
  iconPosition: _propTypes.default.oneOf(['before', 'after']),
  /**
   * Sets the size of the icon
   */
  iconSize: _propTypes.default.string,
  /**
   * Sets the id
   */
  id: _propTypes.default.string,
  /**
   * Visually hidden additional instruction text to help provide screen reader users additional context. For instance, "View details" might be the visible button text, but screenReaderText might add additional instructions such as "for confirmation number C1234567"
   */
  screenReaderText: _propTypes.default.string,
  /**
   * Determines size of link and/or link
   * - **default:** Sets the default link and button size
   * - **compact:** Sets the compact link and button size
   * - **tiny:** Sets the tiny button size
   */
  size: _propTypes.default.oneOf(['default', 'compact', 'tiny']),
  /**
   * Sets the tabIndex, typically 0 or -1, for Aria support
   */
  tabIndex: _propTypes.default.number,
  /**
   * Target attribute for a link (i.e. set to _blank to open in new tab)
   * - **_blank:** yields a link that opens in a new tab
   * - **_self:** yields a link that loads the URL into the same browsing context as the current one. This is the default behavior
   * - **_parent:** yields a link that loads the URL into the parent browsing context of the current one. If there is no parent, this behaves the same way as _self
   * - **_top:** yields a link that loads the URL into the top-level browsing context. If there is no parent, this behaves the same way as _self.
   */
  target: _propTypes.default.oneOf(['_blank', '_self', '_parent', '_top']),
  /**
   * Style of the text for hidden links
   * - **body-lg:** styles the text body-lg
   * - **body:** styles the text body
   * - **body-sm:** styles the text body-sm
   * - **label:** styles the text label
   * - **label-sm:** styles the text label-sm
   * - **title-xlg:** styles the text title-xlg
   * - **title-lg:** styles the text title-lg
   * - **title:** styles the text title
   * - **title-sm:** styles the text title-sm
   * - **headline:** styles the text headline
   * - **headline-lg:** styles the text headline-lg
   * - **headline-sm:** styles the text headline-sm
   * - **display-xlg:** styles the text display-xlg
   * - **display-sm:** styles the text display-sm
   * - **displayBody:** styles the text displayBody
   * - **displayBody-sm:** styles the text displayBody-sm
   */
  textStyle: _propTypes.default.oneOf(['body-lg', 'body', 'body-sm', 'label', 'label-sm', 'title-xlg', 'title-lg', 'title', 'title-sm', 'headline-lg', 'headline', 'headline-sm', 'display-xlg', 'display-lg', 'display', 'display-sm', 'displayBody', 'displayBody-sm']),
  /**
   * Determines variation of link
   * - **primary:** The primary link style
   * - **secondary:** The secondary link style
   * - **tertiary:** The tertiary link style
   * - **arrow:** The arrow link style
   * - **hiddenLink:** The hidden title link style
   * - **absoluteLink:** The absolute link style for cards
   * - **buttonPrimary:** The primary button style
   * - **buttonPrimaryInverse:** The primary inverse button style
   * - **buttonSecondary:** The secondary button style
   * - **buttonTertiary:** The tertiary button style
   * - **buttonAi:** The AI button style
   */
  variant: _propTypes.default.oneOf(['primary', 'secondary', 'tertiary', 'arrow', 'hiddenLink', 'absoluteLink', 'buttonPrimary', 'buttonPrimaryInverse', 'buttonSecondary', 'buttonTertiary', 'buttonAi'])
};

/**
 * Property type definitions
 * @type {object}
 * @property {node} children - set the default value of children
 * @property {boolean} disable - set the default value of disable
 * @property {boolean} fullWidth - Set the default string value for fullWidth
 * @property {string} href - set the default value of href
 * @property {string} iconName - set the default value of iconName
 * @property {string} iconPosition - set the default value of iconPosition
 * @property {bool} isKnockoutLink - set the default value of knockout
 * @property {any} linkRef - Set the default string value for linkRef
 * @property {string} screenReaderText - set the default value of screenReaderText
 * @property {string} size - set the default value of size
 * @property {string} target - set the default value of target
 * @property {string} variant - set the default value of variant
 */

const defaultProps = exports.defaultProps = {
  children: undefined,
  disabled: false,
  fullWidth: false,
  href: '',
  iconName: undefined,
  iconPosition: 'after',
  isKnockoutLink: false,
  linkRef: null,
  screenReaderText: '',
  size: 'default',
  target: undefined,
  variant: 'primary'
};
Link.propTypes = propTypes;
var _default = exports.default = Link;